import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronUp, ChevronDown, ChevronsUpDown, Eye, EyeOff, 
  Settings2, ChevronLeft, ChevronRight, Columns,
  ArrowUpDown, ArrowUp, ArrowDown, RefreshCw
} from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { TableLoader, ListLoader } from './CommonLoader';
import EnhancedDataTableV2 from './EnhancedDataTableV2';

// Determine if we're on a touch device
const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
const dndBackend = isTouchDevice ? TouchBackend : HTML5Backend;

// Draggable column header component
const DraggableColumnHeader = ({ 
  column, 
  index, 
  moveColumn, 
  hidden, 
  onToggleHide, 
  sortConfig,
  onSort 
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'column',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'column',
    hover: (item) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  const handleSort = () => {
    if (column.sortable !== false) {
      onSort(column.key);
    }
  };

  const getSortIcon = () => {
    if (column.sortable === false) return null;
    
    if (sortConfig?.key === column.key) {
      return sortConfig.direction === 'asc' ? (
        <ArrowUp className="w-3 h-3" />
      ) : (
        <ArrowDown className="w-3 h-3" />
      );
    }
    return <ChevronsUpDown className="w-3 h-3 opacity-50" />;
  };

  return (
    <th
      ref={(node) => drag(drop(node))}
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-move ${
        isDragging ? 'opacity-50' : ''
      } ${hidden ? 'opacity-30' : ''} ${column.sortable !== false ? 'hover:bg-gray-100' : ''}`}
      onClick={handleSort}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {column.header || column.label}
          {getSortIcon()}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleHide(column.key);
          }}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      </div>
    </th>
  );
};

/**
 * EnhancedDataTable - Advanced table with sorting, column visibility, and drag-and-drop
 */
const EnhancedDataTable = ({
  columns: initialColumns = [],
  data = [],
  loading = false,
  emptyMessage = "No data found",
  actions,
  onRowClick,
  pagination,
  onPageChange,
  itemName = "items",
  onSort,
  defaultSortKey,
  defaultSortDirection = 'asc',
  className = "",
  showColumnControls = true,
  showRefresh,
  onRefresh
}) => {
  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];
  
  // State management
  const [columns, setColumns] = useState(initialColumns);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [tableKey, setTableKey] = useState(0);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultSortDirection
  });

  // Update columns when initialColumns changes
  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Column management functions
  const moveColumn = (fromIndex, toIndex) => {
    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    setColumns(newColumns);
  };

  const toggleHideColumn = (key) => {
    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(key)) {
      newHiddenColumns.delete(key);
    } else {
      newHiddenColumns.add(key);
    }
    setHiddenColumns(newHiddenColumns);
  };

  const resetColumns = () => {
    setColumns(initialColumns);
    setHiddenColumns(new Set());
    setTableKey(prev => prev + 1); // Force re-render to clear any inline styles
  };

  // Sorting logic
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    // Call parent sort handler if provided
    if (onSort) {
      onSort(key, direction);
    }
  };

  // Sort data locally if no external sort handler
  const sortedData = useMemo(() => {
    if (!sortConfig?.key || onSort) {
      return safeData;
    }

    return [...safeData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [safeData, sortConfig, onSort]);

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-4">
        {loading ? (
          <ListLoader rows={5} />
        ) : sortedData.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8">
            <div className="text-center text-gray-500">
              {emptyMessage}
            </div>
          </div>
        ) : (
          sortedData.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="bg-white rounded-lg shadow p-4 space-y-3 border-l-4 border-primary-500"
              onClick={() => onRowClick && onRowClick(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns
                .filter(col => !hiddenColumns.has(col.key))
                .map((column) => (
                  <div key={column.key} className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-500">
                      {column.header || column.label}:
                    </span>
                    <span className="text-sm text-gray-900 text-right ml-2">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </span>
                  </div>
                ))}
              {actions && (
                <div className="pt-2 border-t border-gray-100 flex justify-end gap-2">
                  {(() => {
                    const result = actions(row);
                    if (Array.isArray(result)) {
                      return result.map((action, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick();
                          }}
                          className={`p-2 rounded-lg transition-colors ${action.className || ''}`}
                          title={action.label}
                        >
                          <action.icon className="w-4 h-4" />
                        </button>
                      ));
                    }
                    return result;
                  })()}
                </div>
              )}
            </div>
          ))
        )}
        
        {/* Mobile Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => onPageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="p-2 rounded-lg border disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              <button
                onClick={() => onPageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="p-2 rounded-lg border disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <DndProvider backend={dndBackend}>
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
        {/* Table Controls */}
        {showColumnControls && (
          <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-700">
              {sortedData.length} {sortedData.length === 1 ? 'item' : itemName}
            </h3>
            <div className="flex items-center gap-2">
              {showRefresh && onRefresh && (
                <button
                  onClick={onRefresh}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Columns className="w-4 h-4" />
                Columns
              </button>
              <button
                onClick={resetColumns}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Column Settings Panel */}
        {showColumnSettings && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <p className="text-xs text-gray-600 mb-3">
              Drag columns to reorder. Click to toggle visibility.
            </p>
            <div className="flex flex-wrap gap-2">
              {columns.map((col) => (
                <button
                  key={col.key}
                  onClick={() => toggleHideColumn(col.key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    hiddenColumns.has(col.key)
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {hiddenColumns.has(col.key) ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                  {col.header || col.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <TableLoader rows={10} columns={columns.filter(c => !hiddenColumns.has(c.key)).length} />
          ) : sortedData.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {emptyMessage}
            </div>
          ) : (
            <table key={tableKey} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column, index) =>
                    !hiddenColumns.has(column.key) ? (
                      <DraggableColumnHeader
                        key={column.key}
                        column={column}
                        index={index}
                        moveColumn={moveColumn}
                        hidden={hiddenColumns.has(column.key)}
                        onToggleHide={toggleHideColumn}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    ) : null
                  )}
                  {actions && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`${onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''} transition-colors`}
                  >
                    {columns.map((column) =>
                      !hiddenColumns.has(column.key) ? (
                        <td key={column.key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {column.render ? column.render(row[column.key], row) : row[column.key]}
                        </td>
                      ) : null
                    )}
                    {actions && (
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap text-right">
                        {(() => {
                          const result = actions(row);
                          if (Array.isArray(result)) {
                            return (
                              <div className="flex justify-end gap-1">
                                {result.map((action, index) => (
                                  <button
                                    key={index}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      action.onClick();
                                    }}
                                    className={`p-1.5 rounded-lg transition-colors ${action.className || 'hover:bg-gray-100'}`}
                                    title={action.label}
                                  >
                                    <action.icon className="w-4 h-4" />
                                  </button>
                                ))}
                              </div>
                            );
                          }
                          return result;
                        })()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} {itemName}
            </div>
            <div className="flex items-center gap-1">
              {/* First page button */}
              <button
                onClick={() => onPageChange(1)}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="First page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Previous page button */}
              <button
                onClick={() => onPageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Page numbers */}
              {getPageNumbers(pagination).map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${index}`} className="w-8 h-8 flex items-center justify-center text-gray-400">...</span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-all ${
                      pageNum === pagination.current_page
                        ? 'text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    style={pageNum === pagination.current_page ? { backgroundColor: '#3872FA' } : {}}
                  >
                    {pageNum}
                  </button>
                )
              ))}
              
              {/* Next page button */}
              <button
                onClick={() => onPageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              
              {/* Last page button */}
              <button
                onClick={() => onPageChange(pagination.last_page)}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Last page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

// Helper function to get page numbers with ellipsis
const getPageNumbers = (pagination) => {
  const { current_page, last_page } = pagination;
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - delta && i <= current_page + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  });

  return rangeWithDots;
};

// Export the enhanced V2 version with all features
export default EnhancedDataTableV2;

// Keep original for backward compatibility if needed
export { EnhancedDataTable as EnhancedDataTableV1 };